import _ from "lodash";
import React from "react";
import {
  SettingsOutlined,
  BooksTwoOutlined,
  HomeOutlined,
  MultipleUsersOutlined,
} from "@toddle-edu/ds-icons";
import moment from "moment";
import { formatDate, getMemoizedLangFunction } from "Utils";
import ACLStore from "lib/aclStore";
import update from "immutability-helper";
import { recordEvent } from "lib/eventTracker";
import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import { STATUS_SETUP_COMPLETE } from "SchoolOnboardSetup/modules/stringConstants";
import {
  getFutureAcademicYearPlanningAllowedStatus,
  getIsCombineCurriculumHomePageEnabled,
} from "Platform/modules/PlatformUtils";
import { isUserTypeStudent } from "AttendanceSummary/modules/Utils";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import ACLStorage from "lib/aclService";

import {
  COMBINE_CURRICULUM_HOME_PAGE_PERMISSION,
  PROGRAMME_WIDE_CONFIGURATIONS_ACCESS_PERMISSION,
} from "Constants/permissionConstants";

import {
  getIsAdminNavigationFor10Accounts,
  getIsNewAdminUIEnabled,
} from "Utils/ACLStoreUtils";
import {
  ADMIN_PORTAL_POC_VARIANT,
  ADMIN_PORTAL_POC,
} from "Constants/adminPortalPocVariant";

const MAX_ORGANIZATION_ACRONYM_LENGTH = 3;

const CURRICULUM_PROGRAM_CONFIGURATIONS = {
  [CURRICULUM_TYPE_PYP]: {
    labelLocale: "common:label_with_home",
    value: null,
    type: "TODDLE_PYP_PLATFORM",
    curriculumProgramType: CURRICULUM_TYPE_PYP,
  },
  [CURRICULUM_TYPE_MYP]: {
    labelLocale: "common:label_with_home",
    value: null,
    type: "TODDLE_MYP_PLATFORM",
    curriculumProgramType: CURRICULUM_TYPE_MYP,
  },
  [CURRICULUM_TYPE_UBD]: {
    labelLocale: "common:label_with_home",
    value: null,
    type: "TODDLE_UBD_PLATFORM",
    curriculumProgramType: CURRICULUM_TYPE_UBD,
  },
  [CURRICULUM_TYPE_DP]: {
    labelLocale: "common:label_with_home",
    value: null,
    type: "TODDLE_DP_PLATFORM",
    curriculumProgramType: CURRICULUM_TYPE_DP,
  },
};

const MENU_ITEM_TYPES = [
  {
    value: "MY_HOME",
    type: "MY_HOME",
    labelLocale: "common:my_home",
    icon: <HomeOutlined />,
    isSchoolNamePrefixRequired: false,
  },
  {
    labelLocale: "common:admin_portal",
    value: "ADMIN_PORTAL",
    type: "ADMIN_PORTAL",
    icon: <SettingsOutlined />,
  },
  {
    labelLocale: "common:admin_portal",
    value: "ACCOUNT_SCHOOL_NAVIGATION",
    type: "ACCOUNT_SCHOOL_NAVIGATION",
    icon: <SettingsOutlined />,
    isSchoolNamePrefixRequired: false,
    iconColor: "violet",
  },
  {
    labelLocale: "common:toddle_library",
    value: "TODDLE_LIBRARY",
    type: "TODDLE_LIBRARY",
    icon: <BooksTwoOutlined />,
    iconColor: "pink",
  },
  {
    labelLocale: "admissions:switch_to_admissions",
    value: "ADMISSIONS",
    type: "ADMISSIONS",
    icon: <MultipleUsersOutlined />,
    iconColor: "blue",
  },
  // POC 1 only — added at the top-level workspace switcher alongside the
  // existing LMS Admin Portal and Admissions entries.
  {
    labelLocale: "common:core_admin_portal",
    value: "CORE_ADMIN_PORTAL",
    type: "CORE_ADMIN_PORTAL",
    icon: <SettingsOutlined />,
    iconColor: "violet",
    isSchoolNamePrefixRequired: false,
  },
];

const isOnboardingComplete = ({
  curriculumProgramId,
  organizationCurriculumPrograms,
}) => {
  const programOnboardingStatus = _.get(
    _.find(
      organizationCurriculumPrograms,
      ({ id }) => id == curriculumProgramId
    ),
    "onboardingStatus",
    STATUS_SETUP_COMPLETE
  );

  return programOnboardingStatus == STATUS_SETUP_COMPLETE;
};

const isVisibleForCurriculumProgram = ({
  isOrganizationFree,
  curriculumProgramId,
  organizationCurriculumPrograms,
}) => {
  const hasAdminPortalAccess = getIsAdminByRole() && !isOrganizationFree;
  const isSnpVisitor = ACLStore.can("VisitorPortal");

  const onboardingCompletion =
    !ACLStore.can("FeatureFlag:SchoolOnBoarding") ||
    isOnboardingComplete({
      curriculumProgramId,
      organizationCurriculumPrograms,
    });

  return (onboardingCompletion || !hasAdminPortalAccess) && !isSnpVisitor;
};

const getDynamicMenuItems = ({ validCurriculumPrograms, userType }) => {
  if (getIsCombineCurriculumHomePageEnabled()) {
    return MENU_ITEM_TYPES;
  }

  const curriculumMenuItems = _.map(
    validCurriculumPrograms,
    ({ id, type, acronym, label, programType, logo }) => {
      const menuItem = CURRICULUM_PROGRAM_CONFIGURATIONS[type];

      return {
        acronym,
        label,
        type: menuItem.type,
        curriculumProgramType: menuItem.curriculumProgramType,
        value: id,
        programType,
        logo,
      };
    }
  );

  let curriculumMenuItemsConditional = curriculumMenuItems;

  if (isUserTypeStudent({ userType })) {
    curriculumMenuItemsConditional =
      _.size(curriculumMenuItems) > 1 ? curriculumMenuItems : [];
  }

  return update(MENU_ITEM_TYPES, {
    $splice: [[1, 0, ...curriculumMenuItemsConditional]],
  });
};

const getMenuItems = ({
  t,
  isOrganizationFree,
  validCurriculumPrograms,
  organizationCurriculumPrograms,
  accountCurriculumPrograms,
  userType,
  isAdminPortalVisible,
  isToddleLibraryVisible,
  organizationId,
  orgName,
  organizationLogo,
  hasAccessToAccountAdminPortal,
}) => {
  const menuItems = getDynamicMenuItems({
    validCurriculumPrograms,
    userType,
  });

  const filteredMenuItems = [];

  _.forEach(menuItems, itemType => {
    switch (itemType.type) {
      case "TODDLE_PYP_PLATFORM":
      case "TODDLE_MYP_PLATFORM":
      case "TODDLE_UBD_PLATFORM":
      case "TODDLE_DP_PLATFORM":
        {
          filteredMenuItems.push({
            ...itemType,
            label: itemType.label,
            visible: isVisibleForCurriculumProgram({
              isOrganizationFree,
              organizationCurriculumPrograms,
              curriculumProgramId: itemType.value,
            }),
            value: itemType.value,
          });
        }
        break;
      case "ADMIN_PORTAL":
        {
          filteredMenuItems.push({
            ...itemType,
            label: t(itemType.labelLocale),
            visible: isAdminPortalVisible && !getIsNewAdminUIEnabled(),
          });
        }
        break;

      case "ACCOUNT_SCHOOL_NAVIGATION": {
        const children = _.chain(validCurriculumPrograms)
          .filter(({ id }) =>
            ACLStorage.getModuleSettingsValueByContext(
              PROGRAMME_WIDE_CONFIGURATIONS_ACCESS_PERMISSION,
              [
                {
                  entityType: "CURRICULUM_PROGRAM",
                  entityId: id,
                },
              ]
            )
          )
          .map(({ id, label, programType, acronym, type, logo }) => ({
            id,
            label,
            programType,
            acronym,
            logo,
            curriculumProgramType: type,
            value: itemType.type,
          }))
          .value();

        /**
         * For non 2.0 accounts, we need to treat organization as a program of type ACCOUNT
         * and add it to the switcher.
         */
        if (
          getIsAdminNavigationFor10Accounts() &&
          hasAccessToAccountAdminPortal
        ) {
          children.splice(0, 0, {
            id: organizationId,
            label: orgName,
            acronym: _.reduce(
              orgName.split(" "),
              (res, word) => res + word.charAt(0),
              ""
            ).slice(0, MAX_ORGANIZATION_ACRONYM_LENGTH),
            attachments: [
              {
                url: organizationLogo,
              },
            ],
            type: CURRICULUM_TYPE_UBD,
            value: itemType.type,
            programType: "ACCOUNT",
            isDummyAccountProgram: true,
          });
        }

        /**
         * For 2.0 accounts, prepend the first curriculum program to the switcher.
         */
        if (
          ACLStore.can("FeatureFlag:EnableAccountConfiguration") &&
          hasAccessToAccountAdminPortal &&
          !_.isEmpty(accountCurriculumPrograms)
        ) {
          children.splice(0, 0, {
            id: _.first(accountCurriculumPrograms).id,
            label: _.first(accountCurriculumPrograms).label,
            programType: _.first(accountCurriculumPrograms).programType,
            value: itemType.type,
            logo: _.first(accountCurriculumPrograms).logo,
            acronym: _.first(accountCurriculumPrograms).acronym,
          });
        }

        filteredMenuItems.push({
          ...itemType,
          label: t(itemType.labelLocale),
          visible: !isOrganizationFree && getIsNewAdminUIEnabled(),
          children,
          isSchoolNamePrefixRequired: false,
        });
        break;
      }
      case "TODDLE_LIBRARY":
        {
          filteredMenuItems.push({
            ...itemType,
            label: t(itemType.labelLocale),
            visible: isToddleLibraryVisible,
            isSchoolNamePrefixRequired: false,
          });
        }
        break;
      case "MY_HOME": {
        filteredMenuItems.push({
          ...itemType,
          label: t(itemType.labelLocale),
          visible: ACLStore.can(COMBINE_CURRICULUM_HOME_PAGE_PERMISSION),
        });
        break;
      }
      case "ADMISSIONS": {
        filteredMenuItems.push({
          ...itemType,
          label: t(itemType.labelLocale),
          visible: ACLStore.can("FeatureFlag:AdmissionsPOC"),
          isSchoolNamePrefixRequired: false,
        });
        break;
      }
      case "CORE_ADMIN_PORTAL": {
        // Visible only in POC 1 of the admin portal restructuring exploration.
        filteredMenuItems.push({
          ...itemType,
          label: t(itemType.labelLocale),
          visible:
            ADMIN_PORTAL_POC_VARIANT === ADMIN_PORTAL_POC.THREE_PORTALS &&
            isAdminPortalVisible,
          isSchoolNamePrefixRequired: false,
        });
        break;
      }
    }
  });

  return filteredMenuItems;
};

export const memoizedGetMenuItems = getMemoizedLangFunction(
  getMenuItems,
  ({
    userType,
    isAdminPage,
    isOrganizationFree,
    validCurriculumPrograms,
    organizationCurriculumPrograms,
    accountCurriculumPrograms,
    isAdminPortalVisible,
    isToddleLibraryVisible,
    organizationId,
    orgName,
    organizationLogo,
    hasAccessToAccountAdminPortal,
  }) =>
    JSON.stringify({
      userType,
      isAdminPage,
      isOrganizationFree,
      isAdminPortalVisible,
      isToddleLibraryVisible,
      organizationId,
      orgName,
      organizationLogo,
      hasAccessToAccountAdminPortal,
      validCurriculumPrograms: _.map(
        validCurriculumPrograms,
        curriculumProgram =>
          _.pick(curriculumProgram, [
            "id",
            "label",
            "acronym",
            "logo",
            "type",
            "programType",
          ])
      ),
      organizationCurriculumPrograms: _.map(
        organizationCurriculumPrograms,
        curriculumProgram =>
          _.pick(curriculumProgram, ["id", "onboardingStatus"])
      ),
      accountCurriculumPrograms: _.map(
        accountCurriculumPrograms,
        curriculumProgram =>
          _.pick(curriculumProgram, [
            "id",
            "label",
            "programType",
            "logo",
            "acronym",
          ])
      ),
    })
);

export const getFilteredMenuItems = ({ menuItems }) => {
  return _.filter(menuItems, "visible");
};

/**
 * e.g academic year start year 2022 end year 2022
 * if so do not show 2022-22
 */
export const isCurrentAcademicYearEndInSameYear = ({ startDate, endDate }) => {
  return _.isEqual(
    formatDate({ date: startDate, format: "YYYY" }),
    formatDate({ date: endDate, format: "YYYY" })
  );
};

export const getAcademicYearLabel = ({ startDate, endDate }) => {
  return isCurrentAcademicYearEndInSameYear({ startDate, endDate })
    ? formatDate({ date: startDate, format: "YYYY" })
    : formatDate({ date: startDate, format: "YYYY" }) +
        "-" +
        formatDate({ date: endDate, format: "YY" });
};

export const getCategorizedMenuItemsList = ({ menuItemsList }) => {
  const groupedObjects = _.groupBy(menuItemsList, menu => {
    if (
      menu.type === "TODDLE_PYP_PLATFORM" ||
      menu.type === "TODDLE_MYP_PLATFORM" ||
      menu.type === "TODDLE_DP_PLATFORM" ||
      menu.type === "TODDLE_UBD_PLATFORM"
    ) {
      return "CURRICULUM_SWITCHER";
    } else {
      return menu.type;
    }
  });

  const categorizedMenuItemsList = _.mapValues(groupedObjects, (group, key) => {
    if (key === "CURRICULUM_SWITCHER") {
      return group.map(details => details);
    } else {
      return group[0];
    }
  });

  return categorizedMenuItemsList;
};

const getFilteredOrganizationAcademicYears = ({
  academicYears,
  currentAcademicYear,
  isAdminPage,
  isAccountOrSchoolPortal,
}) => {
  const filteredYears = _.filter(academicYears, year => {
    if (year.isCurrentAcademicYear) return true;
    return moment(currentAcademicYear.startDate).isAfter(
      moment(year.endDate),
      "day"
    );
  });

  const upcomingAcademicYears = _.filter(academicYears, {
    isUpcomingAcademicYear: true,
  });

  const immediateFutureAcademicYear =
    _.size(upcomingAcademicYears) > 0
      ? _.minBy(upcomingAcademicYears, year => moment(year.startDate).valueOf())
      : null;

  if (
    getFutureAcademicYearPlanningAllowedStatus({
      futureAcademicYear: immediateFutureAcademicYear,
      isAdminPage,
      isAccountOrSchoolPortal,
    })
  ) {
    filteredYears.unshift(immediateFutureAcademicYear);
  }

  return filteredYears;
};

export const getFilteredOrganizationAcademicYearsMemo = _.memoize(
  getFilteredOrganizationAcademicYears,
  ({
    academicYears,
    isAdminPage,
    currentAcademicYear,
    isAccountOrSchoolPortal,
  }) =>
    JSON.stringify({
      yearIds: _.map(academicYears, "id"),
      isAdminPage,
      currentAcademicYearId: _.get(currentAcademicYear, "id"),
      isAccountOrSchoolPortal,
    })
);

export const addMenuEvent = ({ platformType } = {}) => {
  const eventData = { platform_type: platformType };
  recordEvent({
    eventName: "Clicked platform switcher",
    eventData,
  });
};

export const SWITCHER_TOOLTIP_ALIGNMENT = { offset: [-2, 4] };
